import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, Info, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WeeklyPaycheckResult } from "@/hooks/useWeeklyPaycheck";

interface Props {
  open: boolean;
  paycheck: WeeklyPaycheckResult | null;
  careerLabel?: string;
  onClose: () => void;
}

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

export default function WeeklyPaydayModal({ open, paycheck, careerLabel, onClose }: Props) {
  return (
    <AnimatePresence>
      {open && paycheck && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-4 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ scale: 0.92, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            className="relative w-full max-w-md overflow-hidden rounded-3xl border border-primary/30 bg-card/95 p-6 shadow-[0_0_60px_-15px_hsl(var(--primary)/0.6)] backdrop-blur-xl"
          >
            <div className="pointer-events-none absolute inset-x-0 -top-32 h-64 bg-gradient-to-b from-primary/25 via-primary/5 to-transparent blur-2xl" />

            <div className="relative flex flex-col items-center text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/40 bg-primary/10 text-primary">
                <Briefcase className="h-7 w-7" />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80">
                Paycheck received
              </p>
              <h2 className="mt-1 font-heading text-2xl font-bold text-foreground">
                {careerLabel || "Career Paycheck"}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Weekly pay · {paycheck.weekKey}
              </p>
            </div>

            <TooltipProvider delayDuration={200}>
              <div className="relative mt-6 space-y-2 rounded-2xl border border-border/60 bg-muted/30 p-4">
                <Row
                  label="Gross pay"
                  amount={paycheck.weeklyGrossPay}
                  tone="neutral"
                  tooltip="The total amount earned before anything is taken out."
                />
                <Row
                  label="Taxes & withholding"
                  amount={-paycheck.taxesWithheld}
                  tone="deduction"
                  tooltip={`Estimated withholding (~${Math.round(
                    paycheck.estimatedTaxRate * 100,
                  )}%) set aside before pay reaches your wallet.`}
                />
                <Row
                  label="Benefits / retirement"
                  amount={-paycheck.benefitsDeduction}
                  tone="deduction"
                  tooltip="Pre-tax contributions toward benefits or future savings."
                />
                <div className="h-px bg-border/70 my-2" />
                <Row
                  label="Net deposit"
                  amount={paycheck.netPay}
                  tone="net"
                  tooltip="The amount that actually reaches your Sim Wallet — budget from this number."
                />
              </div>
            </TooltipProvider>

            <div className="relative mt-5 flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/10 p-4">
              <DollarSign className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/80">
                  Deposited to Sim Wallet
                </p>
                <p className="font-heading text-xl font-bold text-foreground">
                  {fmt(paycheck.netPay)}
                </p>
              </div>
            </div>

            <p className="relative mt-4 text-xs leading-relaxed text-muted-foreground">
              You earned {fmt(paycheck.weeklyGrossPay)}, but only{" "}
              <span className="font-semibold text-foreground">{fmt(paycheck.netPay)}</span> hit
              your wallet. The difference is what was withheld for taxes, benefits, or future
              planning.
            </p>

            <Button
              onClick={onClose}
              className="relative mt-5 h-11 w-full gap-2 rounded-xl"
              size="lg"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Row({
  label,
  amount,
  tone,
  tooltip,
}: {
  label: string;
  amount: number;
  tone: "neutral" | "deduction" | "net";
  tooltip: string;
}) {
  const isNeg = amount < 0;
  const valueClass =
    tone === "net"
      ? "text-primary font-heading font-bold text-lg"
      : isNeg
        ? "text-destructive font-semibold"
        : "text-foreground font-semibold";
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <span>{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="opacity-70 hover:opacity-100">
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </div>
      <span className={valueClass}>
        {isNeg ? "−" : ""}
        {fmt(Math.abs(amount))}
      </span>
    </div>
  );
}